import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  Box,
  ClickAwayListener,
  Typography,
  useAutocomplete
} from '@mui/material';

import { Icon } from '../Icon';
import useMasterSlave from './useMasterSlave';
import {
  AutocompleteDropdown,
  InputContainer,
  Input,
  AutocompleteItem,
  Divider,
  DropdownList
} from './TenantDropdown.styled';
import TenantDropdownButton from './TenantDropdownButton/TenantDropdownButton';
import { RawStateSite } from '../SitePicker/site.types';

interface IProps {
  isSidebarOpen: boolean;
  toggleSideBar: () => void;
  selected?: RawStateSite;
}

export const TenantDropdown = ({ isSidebarOpen, toggleSideBar, selected }: IProps) => {
  const { masterSlaveList, onSelectMasterSlave } = useMasterSlave();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSidebarOpen) setIsOpen(false);
  }, [isSidebarOpen]);

  const toggleDropdown = () => {
    if (!isSidebarOpen) {
      toggleSideBar();
    }
    setIsOpen(prev => !prev);
  };

  const handleClickAway = (e: MouseEvent | TouchEvent) => {
    const target = e.target as HTMLElement;
    if (dropRef !== null && e.currentTarget !== null && dropRef?.current?.contains(target)) return;
    toggleDropdown();
  };

  const onSelectOption = (
    event: SyntheticEvent<Element, Event>,
    itemData: RawStateSite,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'selectOption') {
      if (itemData.Name !== selected?.Name || itemData.Name === 'All') {
        toggleDropdown();
        onSelectMasterSlave(itemData);
      }
    }
  };

  const onInputChange = (
    event: SyntheticEvent<Element, Event>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'input') setInputValue(newInputValue);
    if (reason === 'reset') setInputValue('');
  };

  const { getRootProps, getInputProps, getListboxProps, getOptionProps, groupedOptions } = useAutocomplete({
    id: 'tenant-autocomplete',
    options: masterSlaveList,
    open: isOpen,
    getOptionLabel: option => option?.Name ?? '',
    isOptionEqualToValue: (option, val) => option?.Name === val?.Name,
    value: selected,
    onChange: onSelectOption,
    inputValue,
    onInputChange
  });

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}
      data-testid="master-slave"
      ref={dropRef}
    >
      <TenantDropdownButton
        open={isSidebarOpen}
        dropdown={isOpen}
        text={selected?.Name || 'All'}
        onClick={toggleDropdown}
        data-testid="master-slave-btn"
        disabled={masterSlaveList.length <= 1}
      />
      {isOpen && (
        <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
          <AutocompleteDropdown open={isOpen && isSidebarOpen} {...getRootProps()} data-testid="master-slave-combo">
            <InputContainer>
              <Icon name="search" />
              <Input autoFocus placeholder="Search site..." {...getInputProps()} data-testid="autocomplete-input" />
            </InputContainer>

            {masterSlaveList.length && masterSlaveList.length > 1 ? (
              <>
                <Divider />
                <DropdownList {...getListboxProps()}>
                  {(groupedOptions as typeof masterSlaveList).map((option, index) => {
                    const isActive = !selected ? option?.Name === 'All' : option?.Name === selected.Name;
                    return (
                      <AutocompleteItem {...getOptionProps({ option, index })} active={isActive} disabled={isActive}>
                        <Typography variant="body4" color="grey.200" noWrap>
                          {option?.Name}
                        </Typography>
                      </AutocompleteItem>
                    );
                  })}
                </DropdownList>
              </>
            ) : null}
          </AutocompleteDropdown>
        </ClickAwayListener>
      )}
    </Box>
  );
};

export default TenantDropdown;
